import React, { useEffect, useState } from 'react';
import { ASSET_URL } from '../../lib/service-registry';

/**
 * Cross-service my tickets panel (MYB-357)
 */

const TICKET_API = `${ASSET_URL}/api/platform/tickets`;

interface Ticket {
  id: string; linearId: string; linearUrl: string; title: string;
  category: string; service: string; status: string; submittedAt: string;
  reporter?: string;
}

const STATUS_BADGE: Record<string, { label: string; bg: string; color: string }> = {
  'Todo': { label: '접수됨', bg: '#e8f0fe', color: '#1a73e8' },
  'Triage': { label: '분류 중', bg: '#e8f0fe', color: '#1a73e8' },
  'In Progress': { label: '처리 중', bg: '#fef7e0', color: '#e37400' },
  'In Review': { label: '검토 중', bg: '#e8f0fe', color: '#1557b0' },
  'Done': { label: '완료', bg: '#e6f4ea', color: '#1e8e3e' },
  'Cancelled': { label: '취소됨', bg: '#f1f3f4', color: '#5f6368' },
};

const SERVICE_LABEL: Record<string, string> = {
  asset: '자산 관리', hr: '인사 관리', finance: '자금 관리', landing: '서비스 허브', other: '기타',
};

interface Props { onClose: () => void; onNewTicket: () => void; }

const MyTicketsPanel: React.FC<Props> = ({ onClose, onNewTicket }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'open' | 'done' | 'all'>('open');

  useEffect(() => {
    setLoading(true);
    fetch(`${TICKET_API}?filter=${filter}&limit=30`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setTickets(d.tickets || []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  }, [filter]);

  const badge = (status: string) => {
    const b = STATUS_BADGE[status] || { label: status, bg: '#f1f3f4', color: '#5f6368' };
    return <span style={{ fontSize: '0.75rem', fontWeight: 500, padding: '2px 8px', borderRadius: '0.75rem', background: b.bg, color: b.color }}>{b.label}</span>;
  };

  const tabs: { key: 'open' | 'done' | 'all'; label: string }[] = [
    { key: 'open', label: '열림' }, { key: 'done', label: '완료' }, { key: 'all', label: '전체' },
  ];

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(32,33,36,0.5)' }} onClick={onClose} />
      <div style={{ position: 'fixed', right: 0, top: 0, bottom: 0, zIndex: 50, width: 400, background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e8eaed' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e8eaed' }}>
          <span style={{ fontSize: '1rem', fontWeight: 500, color: '#202124' }}>이슈 목록</span>
          <button aria-label="닫기" onClick={onClose} style={{ width: 32, height: 32, border: 'none', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', color: '#80868b', cursor: 'pointer' }}>
            <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div style={{ padding: '8px 16px', borderBottom: '1px solid #e8eaed' }}>
          <button onClick={onNewTicket} style={{ width: '100%', padding: '6px 0', border: 'none', borderRadius: '0.5rem', background: '#1a73e8', color: '#fff', fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer' }}>+ 이슈 등록</button>
        </div>
        <div style={{ display: 'flex', gap: 0, padding: '8px 16px 0', borderBottom: '1px solid #e8eaed' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setFilter(t.key)}
              style={{ flex: 1, padding: '8px 0', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: filter === t.key ? 600 : 400, color: filter === t.key ? '#1a73e8' : '#5f6368', borderBottom: filter === t.key ? '2px solid #1a73e8' : '2px solid transparent' }}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#80868b', fontSize: '0.8125rem' }}>로딩 중...</div>
          ) : tickets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <p style={{ fontSize: '0.875rem', color: '#80868b' }}>{filter === 'open' ? '열린 이슈가 없습니다' : filter === 'done' ? '완료된 이슈가 없습니다' : '등록된 이슈가 없습니다'}</p>
              <button onClick={onNewTicket} style={{ marginTop: 12, padding: '6px 16px', border: 'none', borderRadius: '0.5rem', background: '#1a73e8', color: '#fff', fontSize: '0.75rem', cursor: 'pointer' }}>이슈 등록하기</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {tickets.map(t => (
                <a key={t.id} href={t.linearUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'block', padding: 12, border: '1px solid #e8eaed', borderRadius: '0.75rem', background: '#fff', textDecoration: 'none', transition: 'background 0.15s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#5f6368' }}>{SERVICE_LABEL[t.service] || t.service}</span>
                    {badge(t.status)}
                  </div>
                  <p style={{ fontSize: '0.875rem', fontWeight: 500, color: '#202124', margin: 0, lineHeight: 1.4 }}>{t.title}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                    <span style={{ fontSize: '0.75rem', color: '#80868b' }}>{t.reporter} · {new Date(t.submittedAt).toLocaleDateString('ko-KR')}</span>
                    <span style={{ fontSize: '0.75rem', color: '#80868b' }}>{t.linearId}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MyTicketsPanel;
